import {CustomWeaveTypePayload} from '../../../typeViews/customWeaveType.types';
import {
  KeyedDictType,
  TraceCallSchema,
} from '../../wfReactInterface/traceServerClientTypes';
import {ChatCompletion, ChatRequest, Choice, Message} from '../types';

// OpenAI Image Generation types
export interface OpenAIImageData {
  b64_json?: string | CustomWeaveTypePayload | null;
  revised_prompt?: string | null;
  url?: string | CustomWeaveTypePayload | null;
}

export interface OpenAIImageResponse {
  created: number;
  model?: string; // Include model field from response
  data?: OpenAIImageData[];
  usage?: {
    total_tokens?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
    input_tokens?: number;
    output_tokens?: number;
  };
}

export interface OpenAIImageRequest {
  model: string;
  prompt: string;
  n?: number;
}

// Helper function to check if an object is a Weave Content object
const isWeaveContent = (obj: any): obj is CustomWeaveTypePayload => {
  return (
    obj &&
    typeof obj === 'object' &&
    obj._type === 'CustomWeaveType' &&
    obj.weave_type &&
    obj.files &&
    typeof obj.files.content === 'string'
  );
};

export const isTraceCallChatFormatOpenAIImage = (
  call: TraceCallSchema
): boolean => {
  return (
    isTraceCallChatFormatOpenAIImageRequest(call.inputs) ||
    isTraceCallChatFormatOpenAIImageResult(call.output)
  );
};

export const isTraceCallChatFormatOpenAIImageResult = (
  outputs: any
): outputs is OpenAIImageResponse => {
  if (!outputs || typeof outputs !== 'object' || !('created' in outputs)) {
    return false;
  }

  // Check for new simplified format with direct content array
  if ('content' in outputs && Array.isArray(outputs.content)) {
    return outputs.content.every(
      (item: any) =>
        item &&
        typeof item === 'object' &&
        item.type === 'image' &&
        'image' in item
    );
  }

  // Check for legacy format with data array
  if ('data' in outputs && Array.isArray(outputs.data)) {
    return outputs.data.every(
      (item: any) =>
        item &&
        typeof item === 'object' &&
        (('url' in item &&
          (typeof item.url === 'string' || isWeaveContent(item.url))) ||
          ('b64_json' in item &&
            (typeof item.b64_json === 'string' ||
              item.b64_json === null ||
              isWeaveContent(item.b64_json))))
    );
  }

  return false;
};

export const isTraceCallChatFormatOpenAIImageRequest = (
  value: KeyedDictType
): value is OpenAIImageRequest => {
  return (
    value &&
    typeof value === 'object' &&
    'model' in value &&
    typeof value.model === 'string' &&
    (value.model.includes('dall-e') ||
      value.model.includes('dalle') ||
      value.model.includes('gpt-image')) &&
    'prompt' in value &&
    typeof value.prompt === 'string'
  );
};

export const normalizeOpenAIImageRequest = (
  request: OpenAIImageRequest
): ChatRequest => {
  return {
    model: request.model,
    messages: [
      {
        role: 'user',
        content: request.prompt,
      },
    ],
  };
};

export const normalizeOpenAIImageResponse = (
  result: OpenAIImageResponse
): ChatCompletion => {
  const imageMessages: Message[] = (result.data || []).map(
    (imageData, index) => {
      const content: any[] = [];

      if (isWeaveContent(imageData.url)) {
        // Handle Weave Content objects in url field
        content.push({
          type: 'image',
          image: imageData.url, // Use the Weave content structure directly
        });
      } else if (isWeaveContent(imageData.b64_json)) {
        // Handle Weave Content objects in b64_json field
        content.push({
          type: 'image',
          image: imageData.b64_json, // Use the Weave content structure directly
        });
      } else if (imageData.url && typeof imageData.url === 'string') {
        content.push({
          type: 'image_url',
          image_url: {
            url: imageData.url,
          },
        });
      } else if (
        imageData.b64_json &&
        typeof imageData.b64_json === 'string' &&
        imageData.b64_json !== 'Saved as file' &&
        imageData.b64_json !== 'Converted to content object'
      ) {
        // Handle base64 data
        const b64Data = imageData.b64_json as string; // Explicit type assertion after type guard
        let imageUrl: string;
        if (b64Data.startsWith('data:')) {
          imageUrl = b64Data;
        } else {
          const cleanBase64 = b64Data.replace(/\s/g, '');
          let mimeType = 'image/png'; // default
          if (cleanBase64.startsWith('/9j/')) {
            mimeType = 'image/jpeg';
          }
          imageUrl = `data:${mimeType};base64,${cleanBase64}`;
        }

        content.push({
          type: 'image_url',
          image_url: {
            url: imageUrl,
          },
        });
      }

      return {
        role: 'assistant',
        content,
      };
    }
  );

  const choice: Choice = {
    index: 0,
    finish_reason: 'stop',
    message:
      imageMessages.length === 1
        ? imageMessages[0]
        : {
            role: 'assistant',
            content: imageMessages.flatMap(msg =>
              Array.isArray(msg.content) ? msg.content : []
            ),
          },
  };

  return {
    id: `img-${result.created}`,
    model: result.model || '',
    created: result.created,
    usage: {
      completion_tokens:
        result.usage?.completion_tokens || result.usage?.output_tokens || 0,
      prompt_tokens:
        result.usage?.prompt_tokens || result.usage?.input_tokens || 0,
      total_tokens:
        result.usage?.total_tokens ||
        (result.usage?.prompt_tokens || 0) +
          (result.usage?.completion_tokens || 0) ||
        0,
    },
    choices: [choice],
    system_fingerprint: '',
  };
};
